import React from 'react';
import { useT } from '~app/core/BundleProvider';
import withEnterToClick from '~app/core/withEnterToClick';
import { actions as CanvasActions } from '~app/features/canvas';
import { useSelector, useDispatch } from 'react-redux';
import AccordionMenu from './AccordionMenu';

const ClickOrEnterDiv = withEnterToClick('div');

// Error panel in self check mode
const ErrorPanel = () => {
  const t = useT();
  const dispatch = useDispatch();
  const endCheckMode = () => {
    dispatch(CanvasActions.exitComparisonState());
  };
  const workingModelErrors = useSelector(
    state => state.document.present?.workingModelErrors,
  );

  const {
    objectMistakeCount,
    missingObjectsCount,
    flowMistakeCount,
    connectorMistakeCount,
    missingConnectorCount,
  } = workingModelErrors;

  let totalWorkingModelErrors =
    objectMistakeCount +
    missingObjectsCount +
    flowMistakeCount +
    connectorMistakeCount;

  // This does not allow the totalWorkingModelErrors to go below zero
  if (missingConnectorCount > 0) {
    totalWorkingModelErrors = totalWorkingModelErrors + missingConnectorCount;
  }

  const getPanelContents = (errorTypeDescription, arrErrorTypeExamples) => {
    return (
      <>
        <h4>{errorTypeDescription}</h4>
        <ul>
          {arrErrorTypeExamples.map(errorTypeExample => (
            <li key={errorTypeExample}>{errorTypeExample}</li>
          ))}
        </ul>
      </>
    );
  };

  const doctype = useSelector(state => state.document.present?.type);
  const arrObjectMistakeTypes = [];
  if (doctype === 'stockflow') {
    arrObjectMistakeTypes.push(t('incorrect-type-or-unassigned'));
  }
  arrObjectMistakeTypes.push(t('unrequired-but-connected'));

  let connectorMistakeTypesArr = [];
  if (doctype === 'conceptual') {
    connectorMistakeTypesArr = [t('connector-should-not-exist')];
  } else {
    connectorMistakeTypesArr = [
      t('connector-should-not-exist'),
      t('incorrect-connector-type'),
      t('incorrect-terminal'),
    ];
  }

  const errorAccordionContents = {
    objectMistakes:
      objectMistakeCount < 1
        ? null
        : {
            summary: `${objectMistakeCount} ${
              objectMistakeCount === 1
                ? t('object-mistake.singular')
                : t('object-mistake.plural')
            }`,
            panelContents: getPanelContents(
              t('object-mistake-can-be'),
              arrObjectMistakeTypes,
            ),
          },
    missingObjects:
      missingObjectsCount < 1
        ? null
        : {
            summary: `${missingObjectsCount} ${
              missingObjectsCount === 1
                ? t('missing-object.singular')
                : t('missing-object.plural')
            }`,
            panelContents: getPanelContents(t('missing-object-can-be'), [
              t('required-object-in-bank'),
            ]),
          },
    flowMistakes:
      flowMistakeCount < 1
        ? null
        : {
            summary: `${flowMistakeCount} ${
              flowMistakeCount === 1
                ? t('flow-mistake.singular')
                : t('flow-mistake.plural')
            }`,
            panelContents: getPanelContents(t('flow-mistake-can-be'), [
              t('misdirected-flow'),
            ]),
          },
    connectorMistakes:
      connectorMistakeCount < 1
        ? null
        : {
            summary: `${connectorMistakeCount} ${
              connectorMistakeCount === 1
                ? t('connector-mistake.singular')
                : t('connector-mistake.plural')
            }`,
            panelContents: getPanelContents(
              t('connector-mistake-can-be'),
              connectorMistakeTypesArr,
            ),
          },
    missingConnectors:
      missingConnectorCount < 1
        ? null
        : {
            summary: `${missingConnectorCount} ${
              missingConnectorCount === 1
                ? t('missing-connector.singular')
                : t('missing-connector.plural')
            }`,
            panelContents: getPanelContents(t('missing-connector-can-be'), [
              t('missing-connector'),
            ]),
          },
  };

  const totalErrorsNumber =
    totalWorkingModelErrors === 1 ? 'singular' : 'plural';

  return (
    <div
      tabIndex={0}
      aria-label="Check Results Panel"
      className={`ErrorPanel ${
        totalWorkingModelErrors === 0 ? 'noErrors' : ''
      }`}
    >
      <h3 tabIndex={0}>{`${totalWorkingModelErrors} ${t(
        `mistake-found.${totalErrorsNumber}`,
      )}`}</h3>
      <AccordionMenu accordionContents={errorAccordionContents} />
      <ClickOrEnterDiv
        key="close-check-results"
        className="ErrorPanel__button"
        onClick={endCheckMode}
        role="button"
        aria-label={t('close-check-results')}
        tabIndex="0"
      >
        {t('close-check-results')}
      </ClickOrEnterDiv>
    </div>
  );
};

export default ErrorPanel;
